// authForm.ts
// Handles authentication forms (login, register, profile) following the app's hybrid React/TS pattern
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { loginUser, loginUserWithTemporaryPassword, getCurrentUser, logoutUser, changePassword, changePasswordWithAttributes, startTokenMonitoring, stopTokenMonitoring } from '../backendClient';
// ==============================
// ERROR MESSAGE HELPERS
// ==============================
/**
 * Validate password strength based on Cognito User Pool requirements
 */
function validatePassword(password) {
    // Minimum length (adjust based on your User Pool settings)
    if (password.length < 8) {
        return { isValid: false, message: 'Password must be at least 8 characters long' };
    }
    // Check for uppercase letter
    if (!/[A-Z]/.test(password)) {
        return { isValid: false, message: 'Password must contain at least one uppercase letter' };
    }
    // Check for lowercase letter
    if (!/[a-z]/.test(password)) {
        return { isValid: false, message: 'Password must contain at least one lowercase letter' };
    }
    // Check for number
    if (!/[0-9]/.test(password)) {
        return { isValid: false, message: 'Password must contain at least one number' };
    }
    // Check for special character (adjust based on your User Pool settings)
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
        return { isValid: false, message: 'Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)' };
    }
    return { isValid: true, message: '' };
}
/**
 * Convert technical error messages to clear messages for users
 * @param error - Error message or error code from Cognito
 * @param errorCode - Optional Cognito error code for more precise error handling
 */
function getErrorMessage(error, errorCode) {
    // Use error code if available for more precise error detection
    const code = errorCode || '';
    // AWS Cognito specific error codes (most reliable)
    if (code === 'UserNotFoundException' || error.includes('UserNotFoundException') || error.includes('User does not exist')) {
        return 'No account found with this username or email. Please check your credentials or create a new account.';
    }
    if (code === 'NotAuthorizedException' || error.includes('NotAuthorizedException')) {
        // NotAuthorizedException can mean:
        // 1. Incorrect password
        // 2. Account not set up yet (user exists but hasn't completed setup)
        // We'll show a generic message here, and the calling code will check if user exists
        return 'Incorrect password or account not set up. Please check your password or complete account setup.';
    }
    if (code === 'InvalidPasswordException' || error.includes('Invalid password')) {
        return 'The password you entered is incorrect. Please try again.';
    }
    if (code === 'UserNotConfirmedException' || error.includes('User is not confirmed') || error.includes('UserNotConfirmedException')) {
        return 'Please check your email and confirm your account before signing in.';
    }
    if (code === 'TooManyFailedAttemptsException' || error.includes('Password attempts exceeded') || error.includes('TooManyFailedAttemptsException')) {
        return 'Too many failed login attempts. Please try again later.';
    }
    if (code === 'UserLambdaValidationException' || error.includes('User is disabled') || error.includes('UserLambdaValidationException')) {
        return 'Your account has been disabled. Please contact support.';
    }
    if (code === 'PasswordResetRequiredException' || error.includes('PasswordResetRequiredException') || error.includes('Password reset required')) {
        return 'Your password must be reset. Please use "Forgot your password?" to reset it.';
    }
    // Error message patterns (fallback if error code not available)
    if (error.includes('Incorrect username or password') || error.includes('Incorrect username/email or password')) {
        return 'Incorrect username or password. Please check your credentials and try again.';
    }
    if (error.includes('Network error') || error.includes('fetch') || error.includes('Failed to fetch')) {
        return 'Connection error. Please check your internet connection and try again.';
    }
    // Account setup specific error messages
    if (error.includes('User account already exists') || error.includes('Username already exists') || error === 'USERNAME_EXISTS' || error === 'EMAIL_EXISTS') {
        return 'This username or email is already registered. Please use different credentials or try signing in.';
    }
    if (error === 'USERNAME_INVALID') {
        return 'Invalid username format. Please choose a different username.';
    }
    if (error === 'EMAIL_INVALID') {
        return 'Invalid email format. Please enter a valid email address.';
    }
    if (error.includes('UNKNOWN_ERROR')) {
        // Check if it's a password reset error
        if (error.includes('no registered/verified email') || error.includes('phone_number')) {
            return 'Cannot reset password: no verified email or phone number found for this account. Please contact support.';
        }
        return 'Login failed. Please check your username and password.';
    }
    if (error.includes('no registered/verified email') || error.includes('phone_number')) {
        return 'Cannot reset password: no verified email or phone number found for this account. Please contact support.';
    }
    if (error.includes('Invalid email format')) {
        return 'Please enter a valid email address.';
    }
    if (error.includes('Password too short')) {
        return 'Password must be at least 8 characters long.';
    }
    if (error.includes('Password does not meet requirements')) {
        return 'Password must contain at least 8 characters with uppercase, lowercase, and numbers.';
    }
    // Generic fallback
    return error || 'An unexpected error occurred. Please try again.';
}
// ==============================
// AUTHENTICATION STATE MANAGEMENT
// ==============================
// Global authentication state
let currentUser = null;
let isAuthenticated = false;
// Authentication state listeners
const authStateListeners = [];
/**
 * Subscribe to authentication state changes
 */
export function onAuthStateChange(callback) {
    authStateListeners.push(callback);
    return () => {
        const index = authStateListeners.indexOf(callback);
        if (index >= 0)
            authStateListeners.splice(index, 1);
    };
}
/**
 * Notify all listeners of authentication state changes
 */
function notifyAuthStateChange() {
    console.log('🔔 notifyAuthStateChange - user:', currentUser, 'authenticated:', isAuthenticated);
    authStateListeners.forEach(callback => callback(currentUser, isAuthenticated));
}
/**
 * Update authentication state and notify listeners
 */
function setAuthState(user, authenticated) {
    console.log('🔧 setAuthState - setting user:', user, 'authenticated:', authenticated);
    currentUser = user;
    isAuthenticated = authenticated;
    notifyAuthStateChange();
}
/**
 * Get current authentication state
 */
export function getAuthState() {
    return { user: currentUser, isAuthenticated };
}
// ==============================
// LOGIN FORM
// ==============================
/**
 * Initialize login form functionality
 */
export function initLoginForm() {
    const loginForm = document.getElementById('login-form');
    const usernameInput = document.getElementById('login-username');
    const passwordInput = document.getElementById('login-password');
    const loginBtn = document.getElementById('login-btn');
    const loginError = document.getElementById('login-error');
    const registerLink = document.getElementById('register-link');
    if (!loginForm)
        return;
    // Handle login form submission
    loginForm.addEventListener('submit', (e) => __awaiter(this, void 0, void 0, function* () {
        e.preventDefault();
        const username = usernameInput.value.trim();
        const password = passwordInput.value;
        if (!username || !password) {
            showError(loginError, 'Please fill in all fields');
            return;
        }
        loginBtn.disabled = true;
        loginBtn.textContent = 'Signing in...';
        try {
            // Use SRP authentication for normal logins (permanent passwords)
            // This communicates directly with Cognito and never sends password in clear text
            const result = yield loginUser(username, password);
            if (result.success) {
                console.log('🔐 [Frontend] Login successful, result:', result);
                // Check if this is a temporary password response
                if (result.tokens && result.tokens.AuthenticationResult && result.tokens.AuthenticationResult.AccessToken) {
                    console.log('🔐 [Frontend] Normal login - storing tokens');
                    // Normal login - store tokens
                    localStorage.setItem('accessToken', result.tokens.AuthenticationResult.AccessToken);
                    localStorage.setItem('idToken', result.tokens.AuthenticationResult.IdToken);
                    localStorage.setItem('refreshToken', result.tokens.AuthenticationResult.RefreshToken);
                    // Get user info and update state
                    console.log('🔐 [Frontend] Getting current user info...');
                    const userResult = yield getCurrentUser();
                    console.log('🔐 [Frontend] getCurrentUser result:', userResult);
                    if (userResult.success) {
                        console.log('🔐 [Frontend] User info retrieved successfully, updating state...');
                        // Store user data in localStorage for easy access
                        localStorage.setItem('currentUser', JSON.stringify(userResult.user));
                        setAuthState(userResult.user, true);
                        console.log('🔐 [Frontend] setAuthState called with user:', userResult.user);
                        hideAuthForms();
                        showMainApp();
                        // Start token monitoring
                        startTokenMonitoring();
                        console.log('🔐 [Frontend] Login process completed successfully');
                    }
                    else {
                        console.error('🔐 [Frontend] Failed to get user info:', userResult.error);
                        showError(loginError, 'Failed to get user information. Please try again.');
                    }
                }
                else if (result.tokens && result.tokens.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
                    // Temporary password - show password change form
                    showPasswordChangeForm(result.tokens.Session);
                    return;
                }
                else {
                    // Unexpected response structure - log for debugging but don't block
                    console.error('Unexpected login response structure:', result);
                    // Still try to proceed if we have tokens
                    if (result.tokens) {
                        console.log('🔐 [Frontend] Attempting to proceed with available tokens...');
                        const userResult = yield getCurrentUser();
                        if (userResult.success) {
                            localStorage.setItem('currentUser', JSON.stringify(userResult.user));
                            setAuthState(userResult.user, true);
                            hideAuthForms();
                            showMainApp();
                            startTokenMonitoring();
                        }
                        else {
                            showError(loginError, 'Login failed: Invalid response from server');
                        }
                    }
                    else {
                        showError(loginError, 'Login failed: Invalid response from server');
                    }
                }
            }
            else {
                // Check if user has temporary password and needs account setup
                if (result.needsTemporaryPassword) {
                    console.log('🔐 [Frontend] Temporary password detected - redirecting to account setup');
                    showRegisterForm();
                    return;
                }
                // Get error details
                const errorCode = result.errorCode || '';
                const errorMessage = result.error || 'Login failed';
                // Handle specific error cases with more precise messages
                if (errorCode === 'UserNotFoundException' || errorMessage.includes('UserNotFoundException') || errorMessage.includes('User does not exist')) {
                    // User doesn't exist - offer to create account
                    const setupLink = '<a href="#" id="error-setup-link" style="color: var(--primary); text-decoration: underline; cursor: pointer;">Complete Setup</a>';
                    showError(loginError, `No account found. New account? ${setupLink}`, true);
                    // Add click handler for the setup link
                    setTimeout(() => {
                        const setupLinkElement = document.getElementById('error-setup-link');
                        if (setupLinkElement) {
                            setupLinkElement.addEventListener('click', (e) => {
                                e.preventDefault();
                                showRegisterForm();
                            });
                        }
                    }, 100);
                    return;
                }
                // For NotAuthorizedException, check user status to distinguish between:
                // 1. Incorrect password (user exists and is CONFIRMED)
                // 2. Account not set up (user exists but has FORCE_CHANGE_PASSWORD status)
                if (errorCode === 'NotAuthorizedException' || errorMessage.includes('NotAuthorizedException')) {
                    try {
                        const identifier = usernameInput.value.trim();
                        const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier);
                        let usernameToCheck = identifier;
                        // If it's an email, find the username
                        if (isEmail) {
                            const { findUsernameByEmail } = yield import('../backendClient');
                            const usernameResult = yield findUsernameByEmail(identifier);
                            if (usernameResult.success && usernameResult.username) {
                                usernameToCheck = usernameResult.username;
                            }
                        }
                        // Check user status
                        const { getUserPublicInfo } = yield import('../backendClient');
                        const userInfoResult = yield getUserPublicInfo(usernameToCheck);
                        if (userInfoResult.success && userInfoResult.userStatus === 'FORCE_CHANGE_PASSWORD') {
                            // User exists but needs to complete setup
                            const setupLink = '<a href="#" id="error-setup-link" style="color: var(--primary); text-decoration: underline; cursor: pointer;">Complete Setup</a>';
                            showError(loginError, `Incorrect password. If this is a new account, please ${setupLink} first.`, true);
                            // Add click handler for the setup link
                            setTimeout(() => {
                                const setupLinkElement = document.getElementById('error-setup-link');
                                if (setupLinkElement) {
                                    setupLinkElement.addEventListener('click', (e) => {
                                        e.preventDefault();
                                        showRegisterForm();
                                    });
                                }
                            }, 100);
                            return;
                        }
                        else {
                            // User is CONFIRMED or status unknown - just incorrect password
                            showError(loginError, 'Incorrect username or password.');
                            return;
                        }
                    }
                    catch (checkError) {
                        // If check fails, show generic error
                        console.log('Could not verify user status:', checkError);
                        showError(loginError, 'Incorrect username or password.');
                        return;
                    }
                }
                // For other errors, use the improved error message function
                const finalErrorMessage = getErrorMessage(errorMessage, errorCode);
                showError(loginError, finalErrorMessage);
            }
        }
        catch (error) {
            // Show clear error messages
            const errorCode = error.code || error.name || undefined;
            const errorMessage = getErrorMessage(error.message || 'Login failed', errorCode);
            showError(loginError, errorMessage);
        }
        finally {
            loginBtn.disabled = false;
            loginBtn.textContent = 'Sign In';
        }
    }));
    // Handle register link click
    if (registerLink) {
        registerLink.addEventListener('click', (e) => {
            e.preventDefault();
            showRegisterForm();
        });
    }
    // Handle forgot password link click
    const forgotPasswordLink = document.getElementById('forgot-password-link');
    if (forgotPasswordLink) {
        forgotPasswordLink.addEventListener('click', (e) => {
            e.preventDefault();
            showPasswordResetForm();
        });
    }
    // Initialize password toggle functionality
    initPasswordToggle('login-password', 'login-password-toggle');
}
// ==============================
// REGISTER FORM
// ==============================
/**
 * Initialize account setup form functionality (temporary password)
 */
export function initRegisterForm() {
    const registerForm = document.getElementById('register-form');
    const usernameInput = document.getElementById('register-username');
    const emailInput = document.getElementById('register-email');
    const givenNameInput = document.getElementById('register-given-name');
    const familyNameInput = document.getElementById('register-family-name');
    const addressInput = document.getElementById('register-address');
    const birthdateInput = document.getElementById('register-birthdate');
    const tempPasswordInput = document.getElementById('register-temp-password');
    const newPasswordInput = document.getElementById('register-new-password');
    const confirmPasswordInput = document.getElementById('register-confirm-password');
    const registerBtn = document.getElementById('register-btn');
    const registerError = document.getElementById('register-error');
    const loginLink = document.getElementById('login-link');
    if (!registerForm)
        return;
    // Auto-fill form fields when username is entered
    // This retrieves existing user data from Cognito (email, name, etc.)
    if (usernameInput) {
        let usernameTimeout = null;
        usernameInput.addEventListener('input', () => __awaiter(this, void 0, void 0, function* () {
            const username = usernameInput.value.trim();
            // Clear previous timeout
            if (usernameTimeout) {
                clearTimeout(usernameTimeout);
            }
            // Wait 500ms after user stops typing before fetching user info
            usernameTimeout = setTimeout(() => __awaiter(this, void 0, void 0, function* () {
                if (username.length > 0) {
                    try {
                        const { getUserPublicInfo } = yield import('../backendClient');
                        const result = yield getUserPublicInfo(username);
                        if (result.success && result.user) {
                            // Pre-fill form fields with existing user data
                            if (emailInput && result.user.email) {
                                emailInput.value = result.user.email;
                                // Disable email field - cannot be modified for existing users
                                // Cognito does not allow email modification during NEW_PASSWORD_REQUIRED challenge
                                emailInput.disabled = true;
                                emailInput.title = 'Email cannot be modified for existing users';
                            }
                            if (givenNameInput && result.user.givenName) {
                                givenNameInput.value = result.user.givenName;
                            }
                            if (familyNameInput && result.user.familyName) {
                                familyNameInput.value = result.user.familyName;
                            }
                            if (addressInput && result.user.address) {
                                addressInput.value = result.user.address;
                            }
                            if (birthdateInput && result.user.birthdate) {
                                birthdateInput.value = result.user.birthdate;
                            }
                            console.log('✅ [AuthForm] Pre-filled form with user data from Cognito (email is read-only)');
                        }
                        else {
                            // User does not exist - clear email and re-enable field
                            if (emailInput) {
                                emailInput.value = '';
                                emailInput.disabled = false;
                                emailInput.title = '';
                            }
                            console.log('ℹ️ [AuthForm] User not found - email field cleared and editable');
                        }
                    }
                    catch (error) {
                        // User might not exist - clear email and re-enable field
                        if (emailInput) {
                            emailInput.value = '';
                            emailInput.disabled = false;
                            emailInput.title = '';
                        }
                        console.log('ℹ️ [AuthForm] Could not fetch user info - email field cleared and editable');
                    }
                }
                else {
                    // Username is empty - clear email and re-enable field
                    if (emailInput) {
                        emailInput.value = '';
                        emailInput.disabled = false;
                        emailInput.title = '';
                    }
                }
            }), 500);
        }));
    }
    // Handle account setup form submission
    registerForm.addEventListener('submit', (e) => __awaiter(this, void 0, void 0, function* () {
        e.preventDefault();
        const username = usernameInput.value.trim();
        const email = emailInput.value.trim();
        const givenName = givenNameInput.value.trim();
        const familyName = familyNameInput.value.trim();
        const address = addressInput.value.trim();
        const birthdate = birthdateInput.value;
        const tempPassword = tempPasswordInput.value;
        const newPassword = newPasswordInput.value;
        const confirmPassword = confirmPasswordInput.value;
        if (!username || !email || !givenName || !familyName || !address || !birthdate || !tempPassword || !newPassword || !confirmPassword) {
            showError(registerError, 'Please fill in all fields');
            return;
        }
        if (newPassword !== confirmPassword) {
            showError(registerError, 'New passwords do not match');
            return;
        }
        // Validate new password strength
        const passwordValidation = validatePassword(newPassword);
        if (!passwordValidation.isValid) {
            showError(registerError, passwordValidation.message);
            return;
        }
        registerBtn.disabled = true;
        registerBtn.textContent = 'Setting up account...';
        try {
            // Use backend authentication for temporary passwords (ADMIN_USER_PASSWORD_AUTH)
            // This flow supports NEW_PASSWORD_REQUIRED challenge which SRP does not support
            const loginResult = yield loginUserWithTemporaryPassword(username, tempPassword);
            if (!loginResult.success) {
                showError(registerError, 'Invalid username or temporary password');
                return;
            }
            if (loginResult.tokens && loginResult.tokens.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
                // User needs to change password - call change password API with user attributes
                const changeResult = yield changePasswordWithAttributes(newPassword, loginResult.tokens.Session, {
                    email,
                    givenName,
                    familyName,
                    address,
                    birthdate
                }, username // Pass the username to the backend
                );
                if (changeResult.success && changeResult.tokens && changeResult.tokens.AuthenticationResult) {
                    localStorage.setItem('accessToken', changeResult.tokens.AuthenticationResult.AccessToken);
                    localStorage.setItem('idToken', changeResult.tokens.AuthenticationResult.IdToken);
                    localStorage.setItem('refreshToken', changeResult.tokens.AuthenticationResult.RefreshToken);
                    const userResult = yield getCurrentUser();
                    if (userResult.success) {
                        localStorage.setItem('currentUser', JSON.stringify(userResult.user));
                        setAuthState(userResult.user, true);
                        hideAuthForms();
                        showMainApp();
                        startTokenMonitoring();
                        showSuccess(registerError, 'Account setup completed successfully!');
                    }
                    else {
                        showError(registerError, 'Failed to get user information. Please try again.');
                    }
                }
                else {
                    showError(registerError, changeResult.error || 'Failed to change password');
                }
            }
            else if (loginResult.tokens && loginResult.tokens.AuthenticationResult) {
                showError(registerError, 'This account already has a permanent password. Please use the login form.');
            }
            else {
                showError(registerError, 'Invalid response from server. Please try again.');
            }
        }
        catch (error) {
            const errorMessage = getErrorMessage(error.message || 'Account setup failed');
            showError(registerError, errorMessage);
        }
        finally {
            registerBtn.disabled = false;
            registerBtn.textContent = 'Complete Setup';
        }
    }));
    // Handle login link click
    if (loginLink) {
        loginLink.addEventListener('click', (e) => {
            e.preventDefault();
            showLoginForm();
        });
    }
    // Initialize password toggle functionality for all password fields
    initPasswordToggle('register-temp-password', 'register-temp-password-toggle');
    initPasswordToggle('register-new-password', 'register-new-password-toggle');
    initPasswordToggle('register-confirm-password', 'register-confirm-password-toggle');
}
// ==============================
// EMAIL CONFIRMATION FORM (REMOVED - No longer needed)
// ==============================
// ==============================
// USER PROFILE
// ==============================
/**
 * Initialize integrated user profile functionality
 */
export function initUserProfile() {
    const userProfileIntegrated = document.getElementById('user-profile-integrated');
    const dropdownTrigger = document.getElementById('profile-dropdown-trigger');
    const dropdownMenu = document.getElementById('profile-dropdown-menu-elegant');
    const logoutBtn = document.getElementById('dropdown-logout-elegant');
    // Profile display elements
    const profileUsername = document.getElementById('profile-name-inline');
    const profileEmail = document.getElementById('profile-email-inline');
    const profileInitials = document.getElementById('profile-initials-inline');
    if (!userProfileIntegrated)
        return;
    // Toggle dropdown
    if (dropdownTrigger && dropdownMenu) {
        dropdownTrigger.addEventListener('click', (e) => {
            e.stopPropagation();
            const isActive = dropdownTrigger.classList.contains('active');
            if (isActive) {
                dropdownTrigger.classList.remove('active');
                dropdownMenu.classList.remove('show');
            }
            else {
                dropdownTrigger.classList.add('active');
                dropdownMenu.classList.add('show');
            }
        });
        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (!userProfileIntegrated.contains(e.target)) {
                dropdownTrigger.classList.remove('active');
                dropdownMenu.classList.remove('show');
            }
        });
    }
    // Handle logout
    if (logoutBtn) {
        logoutBtn.addEventListener('click', () => __awaiter(this, void 0, void 0, function* () {
            try {
                yield logoutUser();
                // Clear all stored data
                localStorage.removeItem('accessToken');
                localStorage.removeItem('idToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('currentUser');
                localStorage.removeItem('lastUsername'); // Clear stored username
                setAuthState(null, false);
                showAuthForms();
                hideMainApp();
                // Stop token monitoring
                stopTokenMonitoring();
                // Close dropdown
                if (dropdownTrigger && dropdownMenu) {
                    dropdownTrigger.classList.remove('active');
                    dropdownMenu.classList.remove('show');
                }
            }
            catch (error) {
                console.error('Logout error:', error);
                // Force logout even if API call fails
                localStorage.removeItem('accessToken');
                localStorage.removeItem('idToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('currentUser');
                localStorage.removeItem('lastUsername'); // Clear stored username
                setAuthState(null, false);
                showAuthForms();
                hideMainApp();
                // Stop token monitoring
                stopTokenMonitoring();
            }
        }));
    }
    // Update profile display when auth state changes
    onAuthStateChange((user, authenticated) => {
        if (authenticated && user) {
            const username = user.username || 'User';
            const email = user.email || '';
            const initials = getInitials(username);
            // Update profile display
            if (profileUsername)
                profileUsername.textContent = username;
            if (profileEmail)
                profileEmail.textContent = email;
            if (profileInitials)
                profileInitials.textContent = initials;
            userProfileIntegrated.style.display = 'flex';
        }
        else {
            userProfileIntegrated.style.display = 'none';
        }
    });
}
/**
 * Get user initials from username
 */
function getInitials(username) {
    return username
        .split(' ')
        .map(name => name.charAt(0).toUpperCase())
        .join('')
        .substring(0, 2);
}
// ==============================
// AUTHENTICATION GUARDS
// ==============================
/**
 * Check if user is authenticated and redirect if not
 */
export function requireAuth() {
    const token = localStorage.getItem('accessToken');
    if (!token) {
        showAuthForms();
        hideMainApp();
        return false;
    }
    return true;
}
/**
 * Initialize authentication guards
 */
export function initAuthGuards() {
    // Check authentication on page load
    checkAuthStatus();
    // Listen for authentication state changes
    onAuthStateChange((user, authenticated) => {
        console.log('🔔 Auth state changed - user:', user, 'authenticated:', authenticated);
        if (authenticated) {
            console.log('✅ User authenticated - hiding auth forms, showing main app');
            hideAuthForms();
            showMainApp();
            // Start token monitoring
            startTokenMonitoring();
        }
        else {
            console.log('❌ User not authenticated - showing auth forms, hiding main app');
            showAuthForms();
            hideMainApp();
            // Stop token monitoring
            stopTokenMonitoring();
            // Ensure centering is applied after a short delay
            setTimeout(() => centerAuthForms(), 50);
        }
    });
    // Listen for window resize to maintain centering
    window.addEventListener('resize', () => {
        const authContainer = document.getElementById('auth-container');
        if (authContainer && authContainer.style.display !== 'none') {
            centerAuthForms();
        }
    });
}
/**
 * Check current authentication status
 */
function checkAuthStatus() {
    return __awaiter(this, void 0, void 0, function* () {
        const token = localStorage.getItem('accessToken');
        console.log('🔍 checkAuthStatus - token exists:', !!token);
        if (!token) {
            console.log('❌ No token found, setting auth state to false');
            setAuthState(null, false);
            // Ensure auth forms are properly centered when showing them
            setTimeout(() => showAuthForms(), 10);
            return;
        }
        try {
            console.log('🔍 Validating token with backend...');
            const result = yield getCurrentUser();
            console.log('🔍 getCurrentUser result:', result);
            if (result.success) {
                console.log('✅ User authenticated, setting auth state to true');
                setAuthState(result.user, true);
                // Start token monitoring
                startTokenMonitoring();
            }
            else {
                console.log('❌ Token invalid, clearing tokens');
                // Token is invalid, clear it
                localStorage.removeItem('accessToken');
                localStorage.removeItem('idToken');
                localStorage.removeItem('refreshToken');
                setAuthState(null, false);
                // Stop token monitoring
                stopTokenMonitoring();
            }
        }
        catch (error) {
            console.error('❌ Auth check failed:', error);
            setAuthState(null, false);
        }
    });
}
// ==============================
// PASSWORD CHANGE FORM
// ==============================
/**
 * Show password change form for temporary passwords
 */
function showPasswordChangeForm(session) {
    // Store session for password change
    localStorage.setItem('tempPasswordSession', session);
    // Hide all forms and show password change form
    hideAllAuthForms();
    const passwordChangeContainer = document.getElementById('password-change-container');
    if (passwordChangeContainer)
        passwordChangeContainer.style.display = 'block';
}
/**
 * Initialize password change form functionality
 */
export function initPasswordChangeForm() {
    const passwordChangeForm = document.getElementById('password-change-form');
    const newPasswordInput = document.getElementById('new-password');
    const confirmPasswordInput = document.getElementById('confirm-new-password');
    const changePasswordBtn = document.getElementById('change-password-btn');
    const passwordChangeError = document.getElementById('password-change-error');
    const backToLoginLink = document.getElementById('back-to-login-from-password-change');
    if (!passwordChangeForm)
        return;
    // Handle password change form submission
    passwordChangeForm.addEventListener('submit', (e) => __awaiter(this, void 0, void 0, function* () {
        e.preventDefault();
        const newPassword = newPasswordInput.value;
        const confirmPassword = confirmPasswordInput.value;
        if (!newPassword || !confirmPassword) {
            showError(passwordChangeError, 'Please fill in all fields');
            return;
        }
        if (newPassword !== confirmPassword) {
            showError(passwordChangeError, 'Passwords do not match');
            return;
        }
        // Validate password strength
        const passwordValidation = validatePassword(newPassword);
        if (!passwordValidation.isValid) {
            showError(passwordChangeError, passwordValidation.message);
            return;
        }
        changePasswordBtn.disabled = true;
        changePasswordBtn.textContent = 'Changing password...';
        try {
            const session = localStorage.getItem('tempPasswordSession');
            if (!session) {
                showError(passwordChangeError, 'Session expired. Please try logging in again.');
                return;
            }
            const result = yield changePassword(newPassword, session);
            if (result.success) {
                showSuccess(passwordChangeError, 'Password changed successfully! You can now sign in.');
                // Clear temporary session
                localStorage.removeItem('tempPasswordSession');
                // Hide password change form and show login form
                setTimeout(() => {
                    hidePasswordChangeForm();
                    showLoginForm();
                }, 2000);
            }
            else {
                const errorMessage = getErrorMessage(result.error || 'Password change failed');
                showError(passwordChangeError, errorMessage);
            }
        }
        catch (error) {
            const errorMessage = getErrorMessage(error.message || 'Password change failed');
            showError(passwordChangeError, errorMessage);
        }
        finally {
            changePasswordBtn.disabled = false;
            changePasswordBtn.textContent = 'Change Password';
        }
    }));
    // Handle back to login link
    if (backToLoginLink) {
        backToLoginLink.addEventListener('click', (e) => {
            e.preventDefault();
            hidePasswordChangeForm();
            showLoginForm();
        });
    }
    // Initialize password toggle functionality
    initPasswordToggle('new-password', 'new-password-toggle');
    initPasswordToggle('confirm-new-password', 'confirm-new-password-toggle');
}
/**
 * Hide password change form
 */
function hidePasswordChangeForm() {
    const passwordChangeContainer = document.getElementById('password-change-container');
    if (passwordChangeContainer)
        passwordChangeContainer.style.display = 'none';
}
// ==============================
// PASSWORD RESET FORMS
// ==============================
/**
 * Hide all authentication forms
 */
function hideAllAuthForms() {
    const loginContainer = document.getElementById('login-container');
    const registerContainer = document.getElementById('register-container');
    const passwordChangeContainer = document.getElementById('password-change-container');
    const passwordResetContainer = document.getElementById('password-reset-container');
    const passwordResetConfirmContainer = document.getElementById('password-reset-confirm-container');
    if (loginContainer)
        loginContainer.style.display = 'none';
    if (registerContainer)
        registerContainer.style.display = 'none';
    if (passwordChangeContainer)
        passwordChangeContainer.style.display = 'none';
    if (passwordResetContainer)
        passwordResetContainer.style.display = 'none';
    if (passwordResetConfirmContainer)
        passwordResetConfirmContainer.style.display = 'none';
}
/**
 * Show password reset request form
 */
function showPasswordResetForm() {
    hideAllAuthForms();
    const passwordResetContainer = document.getElementById('password-reset-container');
    if (passwordResetContainer)
        passwordResetContainer.style.display = 'block';
}
/**
 * Show password reset confirmation form
 */
function showPasswordResetConfirmForm() {
    hideAllAuthForms();
    const passwordResetConfirmContainer = document.getElementById('password-reset-confirm-container');
    if (passwordResetConfirmContainer)
        passwordResetConfirmContainer.style.display = 'block';
}
/**
 * Initialize password reset request form
 */
export function initPasswordResetForm() {
    const passwordResetForm = document.getElementById('password-reset-form');
    const resetUsernameInput = document.getElementById('reset-username');
    const resetRequestBtn = document.getElementById('reset-request-btn');
    const passwordResetError = document.getElementById('password-reset-error');
    const backToLoginLink = document.getElementById('back-to-login-from-reset');
    if (!passwordResetForm)
        return;
    passwordResetForm.addEventListener('submit', (e) => __awaiter(this, void 0, void 0, function* () {
        e.preventDefault();
        const username = resetUsernameInput.value.trim();
        if (!username) {
            showError(passwordResetError, 'Please enter your username');
            return;
        }
        resetRequestBtn.disabled = true;
        resetRequestBtn.textContent = 'Sending...';
        try {
            const { forgotPassword } = yield import('../backendClient');
            const result = yield forgotPassword(username);
            if (result.success) {
                // Store username for confirmation form
                localStorage.setItem('resetUsername', username);
                showSuccess(passwordResetError, 'Reset code sent to your email!');
                setTimeout(() => {
                    showPasswordResetConfirmForm();
                }, 2000);
            }
            else {
                const errorMessage = getErrorMessage(result.error || 'Failed to send reset code');
                showError(passwordResetError, errorMessage);
            }
        }
        catch (error) {
            const errorMessage = getErrorMessage(error.message || 'Failed to send reset code');
            showError(passwordResetError, errorMessage);
        }
        finally {
            resetRequestBtn.disabled = false;
            resetRequestBtn.textContent = 'Send Reset Code';
        }
    }));
    // Handle back to login link
    if (backToLoginLink) {
        backToLoginLink.addEventListener('click', (e) => {
            e.preventDefault();
            showLoginForm();
        });
    }
}
/**
 * Initialize password reset confirmation form
 */
export function initPasswordResetConfirmForm() {
    const passwordResetConfirmForm = document.getElementById('password-reset-confirm-form');
    const resetCodeInput = document.getElementById('reset-code');
    const resetNewPasswordInput = document.getElementById('reset-new-password');
    const resetConfirmPasswordInput = document.getElementById('reset-confirm-password');
    const resetConfirmBtn = document.getElementById('reset-confirm-btn');
    const passwordResetConfirmError = document.getElementById('password-reset-confirm-error');
    const backToResetRequestLink = document.getElementById('back-to-reset-request');
    if (!passwordResetConfirmForm)
        return;
    passwordResetConfirmForm.addEventListener('submit', (e) => __awaiter(this, void 0, void 0, function* () {
        e.preventDefault();
        const resetCode = resetCodeInput.value.trim();
        const newPassword = resetNewPasswordInput.value;
        const confirmPassword = resetConfirmPasswordInput.value;
        const username = localStorage.getItem('resetUsername');
        if (!resetCode || !newPassword || !confirmPassword) {
            showError(passwordResetConfirmError, 'Please fill in all fields');
            return;
        }
        if (!username) {
            showError(passwordResetConfirmError, 'Session expired. Please start over.');
            return;
        }
        if (newPassword !== confirmPassword) {
            showError(passwordResetConfirmError, 'Passwords do not match');
            return;
        }
        // Validate password strength
        const passwordValidation = validatePassword(newPassword);
        if (!passwordValidation.isValid) {
            showError(passwordResetConfirmError, passwordValidation.message);
            return;
        }
        resetConfirmBtn.disabled = true;
        resetConfirmBtn.textContent = 'Resetting...';
        try {
            const { confirmForgotPassword } = yield import('../backendClient');
            const result = yield confirmForgotPassword(username, resetCode, newPassword);
            if (result.success) {
                showSuccess(passwordResetConfirmError, 'Password reset successfully! You can now sign in.');
                // Clear stored username
                localStorage.removeItem('resetUsername');
                setTimeout(() => {
                    showLoginForm();
                }, 2000);
            }
            else {
                const errorMessage = getErrorMessage(result.error || 'Failed to reset password');
                showError(passwordResetConfirmError, errorMessage);
            }
        }
        catch (error) {
            const errorMessage = getErrorMessage(error.message || 'Failed to reset password');
            showError(passwordResetConfirmError, errorMessage);
        }
        finally {
            resetConfirmBtn.disabled = false;
            resetConfirmBtn.textContent = 'Reset Password';
        }
    }));
    // Handle back to reset request link
    if (backToResetRequestLink) {
        backToResetRequestLink.addEventListener('click', (e) => {
            e.preventDefault();
            showPasswordResetForm();
        });
    }
    // Initialize password toggle functionality
    initPasswordToggle('reset-new-password', 'reset-new-password-toggle');
    initPasswordToggle('reset-confirm-password', 'reset-confirm-password-toggle');
}
// ==============================
// UI HELPERS
// ==============================
function showError(element, message, allowHTML = false) {
    if (allowHTML) {
        element.innerHTML = message;
    }
    else {
        element.textContent = message;
    }
    element.className = 'error-message';
    element.style.display = 'block';
}
function showSuccess(element, message) {
    element.textContent = message;
    element.className = 'success-message';
    element.style.display = 'block';
}
function showLoginForm() {
    hideAllAuthForms();
    const loginContainer = document.getElementById('login-container');
    if (loginContainer)
        loginContainer.style.display = 'block';
    clearLoginForm();
}
function showRegisterForm() {
    hideAllAuthForms();
    const registerContainer = document.getElementById('register-container');
    if (registerContainer)
        registerContainer.style.display = 'block';
    clearRegisterForm();
    // Re-enable email field in case it was disabled for a previous user
    const emailInput = document.getElementById('register-email');
    if (emailInput) {
        emailInput.disabled = false;
        emailInput.title = '';
    }
}
// Email confirmation functions removed - no longer needed
function clearLoginForm() {
    const usernameInput = document.getElementById('login-username');
    const passwordInput = document.getElementById('login-password');
    // Clear both username and password
    if (usernameInput) {
        usernameInput.value = '';
        console.log('Username field cleared');
    }
    if (passwordInput) {
        passwordInput.value = '';
        console.log('Password field cleared');
    }
    // Also clear any error messages
    const loginError = document.getElementById('login-error');
    if (loginError) {
        loginError.style.display = 'none';
        loginError.textContent = '';
    }
}
function clearRegisterForm() {
    const usernameInput = document.getElementById('register-username');
    const emailInput = document.getElementById('register-email');
    const givenNameInput = document.getElementById('register-given-name');
    const familyNameInput = document.getElementById('register-family-name');
    const addressInput = document.getElementById('register-address');
    const birthdateInput = document.getElementById('register-birthdate');
    const tempPasswordInput = document.getElementById('register-temp-password');
    const newPasswordInput = document.getElementById('register-new-password');
    const confirmPasswordInput = document.getElementById('register-confirm-password');
    // Clear all fields and re-enable email field
    if (usernameInput)
        usernameInput.value = '';
    if (emailInput) {
        emailInput.value = '';
        emailInput.disabled = false;
        emailInput.title = '';
    }
    if (givenNameInput)
        givenNameInput.value = '';
    if (familyNameInput)
        familyNameInput.value = '';
    if (addressInput)
        addressInput.value = '';
    if (birthdateInput)
        birthdateInput.value = '';
    if (tempPasswordInput)
        tempPasswordInput.value = '';
    if (newPasswordInput)
        newPasswordInput.value = '';
    if (confirmPasswordInput)
        confirmPasswordInput.value = '';
    // Clear error messages
    const registerError = document.getElementById('register-error');
    if (registerError) {
        registerError.style.display = 'none';
        registerError.textContent = '';
    }
}
function centerAuthForms() {
    const authContainer = document.getElementById('auth-container');
    if (authContainer) {
        // Force centering by ensuring flex properties are applied
        authContainer.style.display = 'flex';
        authContainer.style.alignItems = 'center';
        authContainer.style.justifyContent = 'center';
        authContainer.style.minHeight = '100vh';
        authContainer.style.width = '100%';
        authContainer.style.position = 'fixed';
        authContainer.style.top = '0';
        authContainer.style.left = '0';
    }
}
function showAuthForms() {
    centerAuthForms();
    showLoginForm(); // Show login form and clear it
    // Hide registration form (registration disabled)
    const registerContainer = document.getElementById('register-container');
    if (registerContainer) {
        registerContainer.style.display = 'none';
    }
}
function hideAuthForms() {
    const authContainer = document.getElementById('auth-container');
    if (authContainer)
        authContainer.style.display = 'none';
}
function showMainApp() {
    const mainApp = document.getElementById('main-layout');
    console.log('🔍 showMainApp called, mainApp element:', mainApp);
    if (mainApp) {
        mainApp.style.display = 'grid'; // Use grid instead of block
        console.log('✅ Main app shown');
        // Force re-initialization of Mol* viewer after a short delay
        setTimeout(() => {
            console.log('🔧 Re-initializing Mol* viewer...');
            const viewerContainer = document.getElementById('viewer-container');
            if (viewerContainer) {
                console.log('✅ Viewer container found:', viewerContainer);
                // Trigger a resize event to help Mol* viewer initialize properly
                window.dispatchEvent(new Event('resize'));
            }
            else {
                console.error('❌ Viewer container not found');
            }
        }, 100);
    }
    else {
        console.error('❌ Main app element not found');
    }
}
function hideMainApp() {
    const mainApp = document.getElementById('main-layout');
    if (mainApp)
        mainApp.style.display = 'none';
}
// ==============================
// PASSWORD TOGGLE FUNCTIONALITY
// ==============================
/**
 * Initialize password toggle functionality for a password input
 * @param inputId - ID of the password input element
 * @param toggleId - ID of the toggle button element
 */
function initPasswordToggle(inputId, toggleId) {
    const passwordInput = document.getElementById(inputId);
    const toggleButton = document.getElementById(toggleId);
    if (!passwordInput || !toggleButton)
        return;
    let isPasswordVisible = false;
    let holdTimer = null;
    let isHolding = false;
    // Click to toggle
    toggleButton.addEventListener('click', (e) => {
        e.preventDefault();
        togglePasswordVisibility(passwordInput, toggleButton, !isPasswordVisible);
        isPasswordVisible = !isPasswordVisible;
    });
    // Mouse down - start hold timer
    toggleButton.addEventListener('mousedown', (e) => {
        e.preventDefault();
        isHolding = true;
        holdTimer = setTimeout(() => {
            if (isHolding) {
                passwordInput.type = 'text';
                toggleButton.classList.add('showing');
                isPasswordVisible = true;
            }
        }, 200); // 200ms delay
    });
    // Mouse up - hide if was showing from hold
    toggleButton.addEventListener('mouseup', (e) => {
        e.preventDefault();
        if (isHolding && holdTimer) {
            clearTimeout(holdTimer);
            holdTimer = null;
        }
        isHolding = false;
        if (isPasswordVisible) {
            passwordInput.type = 'password';
            toggleButton.classList.remove('showing');
            isPasswordVisible = false;
        }
    });
    // Mouse leave - hide password if showing from hold
    toggleButton.addEventListener('mouseleave', () => {
        if (isHolding && holdTimer) {
            clearTimeout(holdTimer);
            holdTimer = null;
        }
        isHolding = false;
        if (isPasswordVisible) {
            passwordInput.type = 'password';
            toggleButton.classList.remove('showing');
            isPasswordVisible = false;
        }
    });
    // Touch events for mobile
    toggleButton.addEventListener('touchstart', (e) => {
        e.preventDefault();
        isHolding = true;
        holdTimer = setTimeout(() => {
            if (isHolding) {
                passwordInput.type = 'text';
                toggleButton.classList.add('showing');
                isPasswordVisible = true;
            }
        }, 200);
    });
    toggleButton.addEventListener('touchend', (e) => {
        e.preventDefault();
        if (isHolding && holdTimer) {
            clearTimeout(holdTimer);
            holdTimer = null;
        }
        isHolding = false;
        if (isPasswordVisible) {
            passwordInput.type = 'password';
            toggleButton.classList.remove('showing');
            isPasswordVisible = false;
        }
    });
    // Prevent context menu on long press
    toggleButton.addEventListener('contextmenu', (e) => {
        e.preventDefault();
    });
}
/**
 * Toggle password visibility
 * @param input - Password input element
 * @param button - Toggle button element
 * @param show - Whether to show password (true) or hide it (false)
 */
function togglePasswordVisibility(input, button, show) {
    if (show) {
        input.type = 'text';
        button.classList.add('showing');
    }
    else {
        input.type = 'password';
        button.classList.remove('showing');
    }
}
